import { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Stack,
    Typography,
    Box,
    Button,
    TextField,
    Switch,
    Table,
    TableHead,
    TableRow,
    TableBody,
    TableCell,
    IconButton,
    CircularProgress,
    Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { API_URL } from '../../api';
import { useConfirm } from '../../components/ConfirmDialog';
import HowItWorksCard from '../../components/HowItWorksCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

export default function LevelsPage() {
    const confirm = useConfirm();
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const [newName, setNewName] = useState('');
    const [newSort, setNewSort] = useState(0);
    const [busy, setBusy] = useState(false);

    const [editId, setEditId] = useState(null);
    const [editName, setEditName] = useState('');
    const [editSort, setEditSort] = useState(0);

    const load = () => {
        setLoading(true);
        axios
            .get(`${API_URL}/api/levels`)
            .then((res) => setLevels(res.data || []))
            .catch(() => setLevels([]))
            .finally(() => setLoading(false));
    };

    useEffect(load, []);

    const create = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setBusy(true);
        setError('');
        try {
            await axios.post(
                `${API_URL}/api/levels`,
                { name: newName.trim(), sortOrder: Number(newSort) || 0 },
                authHeaders()
            );
            setNewName('');
            setNewSort(0);
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to add.');
        } finally {
            setBusy(false);
        }
    };

    const toggleActive = async (level) => {
        try {
            await axios.patch(
                `${API_URL}/api/levels/${level._id}`,
                { active: !level.active },
                authHeaders()
            );
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update.');
        }
    };

    const remove = async (id) => {
        if (!(await confirm({ title: 'Delete level', message: 'Tutors who picked it will keep it in their profile until they re-save.', confirmLabel: 'Delete', danger: true }))) return;
        try {
            await axios.delete(`${API_URL}/api/levels/${id}`, authHeaders());
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete.');
        }
    };

    const startEdit = (level) => {
        setEditId(level._id);
        setEditName(level.name);
        setEditSort(level.sortOrder ?? 0);
    };
    const cancelEdit = () => setEditId(null);
    const saveEdit = async (id) => {
        try {
            await axios.patch(
                `${API_URL}/api/levels/${id}`,
                { name: editName.trim(), sortOrder: Number(editSort) || 0 },
                authHeaders()
            );
            cancelEdit();
            load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        }
    };

    const seedYears = async () => {
        if (!(await confirm({ title: 'Add all school-year levels', message: 'Add Year 4 through Year 13 to the list? Duplicates will be skipped.', confirmLabel: 'Add' }))) return;
        const existing = new Set(levels.map((l) => l.name.toLowerCase()));
        let added = 0;
        for (let y = 4; y <= 13; y += 1) {
            const name = `Year ${y}`;
            if (existing.has(name.toLowerCase())) continue;
            try {
                await axios.post(
                    `${API_URL}/api/levels`,
                    { name, sortOrder: y },
                    authHeaders()
                );
                added += 1;
            } catch {
                /* skip individual failures */
            }
        }
        setMessage(`Added ${added} level${added === 1 ? '' : 's'}.`);
        load();
    };

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4">Levels</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                        Levels appear in tutor profiles ("Subjects you teach"), parent/student profiles,
                        and class-level filters. Add the labels you want parents and tutors to pick from
                        (e.g. <em>Year 4</em>, <em>Year 5</em>, … or keep the legacy KS2 / KS3 / GCSE / A-Level).
                        Deactivating a level hides it from new pickers without losing historical selections.
                    </Typography>
                    
                </Box>
                <Button variant="outlined" onClick={seedYears}>Seed Year 4–13</Button>
            </Stack>
            <Box sx={{ mb: 3 }}>
                <HowItWorksCard>
                        <Typography variant="caption" component="div" color="text.secondary">
                            <strong>1.</strong> Levels are the coarse buckets (KS2, KS3, GCSE, A-Level) used to group classes, tutors, and students.<br />
                            <strong>2.</strong> Add or edit a level here. The list is intentionally small — most everyday grouping is done via year groups, not levels.<br />
                            <strong>3.</strong> Removing a level doesn't retroactively change historical records — they keep whatever level they were tagged with.<br />
                        </Typography>
                    </HowItWorksCard>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

            <Card sx={{ p: 3, mb: 3 }}>
                <Box component="form" onSubmit={create} sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="New level"
                        placeholder="e.g. Year 4"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        size="small"
                        sx={{ flex: 1 }}
                    />
                    <TextField
                        label="Sort order"
                        type="number"
                        value={newSort}
                        onChange={(e) => setNewSort(e.target.value)}
                        size="small"
                        sx={{ width: 140 }}
                    />
                    <Button type="submit" variant="contained" disabled={busy}>Add</Button>
                </Box>
            </Card>

            <Card>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                ) : (
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell sx={{ width: 120 }}>Sort order</TableCell>
                                <TableCell sx={{ width: 100 }}>Active</TableCell>
                                <TableCell align="right" sx={{ width: 140 }} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {levels.map((l) => (
                                <TableRow key={l._id}>
                                    <TableCell>
                                        {editId === l._id ? (
                                            <TextField
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                size="small"
                                                fullWidth
                                                autoFocus
                                            />
                                        ) : (
                                            l.name
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editId === l._id ? (
                                            <TextField
                                                type="number"
                                                value={editSort}
                                                onChange={(e) => setEditSort(e.target.value)}
                                                size="small"
                                            />
                                        ) : (
                                            l.sortOrder ?? 0
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Switch checked={l.active} onChange={() => toggleActive(l)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        {editId === l._id ? (
                                            <>
                                                <IconButton onClick={() => saveEdit(l._id)} color="primary"><SaveIcon /></IconButton>
                                                <IconButton onClick={cancelEdit}><CloseIcon /></IconButton>
                                            </>
                                        ) : (
                                            <>
                                                <IconButton onClick={() => startEdit(l)}><EditIcon /></IconButton>
                                                <IconButton onClick={() => remove(l._id)}><DeleteIcon /></IconButton>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {levels.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                                            No levels yet — click "Seed Year 4–13" or add one above.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Card>
        </Container>
    );
}